/**
 * 選択状態管理フック（TanStack Query + Optimistic UI）
 *
 * @description
 * SOAP 選択状態を TanStack Query のキャッシュで管理し、
 * Optimistic Update で即座に UI を更新するフック群。
 *
 * 設計意図:
 * - SelectionContext を置き換え、TanStack Query パターンに統一
 * - content をキャッシュせず、常にマスターデータから取得
 * - 選択変更時に自動でサーバー同期
 *
 * @see ADR-055: SelectionContext の Content 同期問題の根本的解決
 */

import { useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@/shared/hooks/queries/queryKeys'
import { updateSOAP, getYakurekiDetail } from '@/shared/apis/yakureki'
import {
  SOAP_SECTION_KEYS,
  SoapSectionKeys,
  SoapContent,
  SoapContentItem,
  Selection,
  HeaderSelection,
} from '@/shared/types/yakurekiSoap'
import { YakurekiDetail } from '@/shared/types/yakurekiCore'

// ========================================
// Constants
// ========================================

// ========================================
// Helper Functions
// ========================================

/**
 * 空の Selection を作成
 */
const createEmptySelection = (): Selection => ({
  subjectives: new Set<number>(),
  objectives: new Set<number>(),
  assessments: new Set<number>(),
  educational_plans: new Set<number>(),
  care_plans: new Set<number>(),
  observational_plans: new Set<number>(),
})

/**
 * 空の HeaderSelection を作成
 */
const createEmptyHeaderSelection = (): HeaderSelection =>
  new Set<SoapSectionKeys>()

/**
 * SoapContent から Selection を計算
 * is_selected フラグに基づいて選択インデックスを抽出
 */
const computeSelectionFromSoapContent = (
  soapContent: SoapContent | undefined
): Selection => {
  if (!soapContent) {
    return createEmptySelection()
  }

  const selection = createEmptySelection()

  for (const key of SOAP_SECTION_KEYS) {
    const items = soapContent[key] || []
    items.forEach((item: SoapContentItem, index: number) => {
      if (item.is_selected) {
        selection[key].add(index)
      }
    })
  }

  return selection
}

/**
 * SoapContent から HeaderSelection を計算
 * セクションに選択されたアイテムがあればヘッダーも選択とみなす
 */
const computeHeaderSelectionFromSoapContent = (
  soapContent: SoapContent | undefined,
  selection: Selection
): HeaderSelection => {
  if (!soapContent) {
    return createEmptyHeaderSelection()
  }

  const headerSelection = createEmptyHeaderSelection()

  for (const key of SOAP_SECTION_KEYS) {
    const items = soapContent[key] || []
    // セクションにアイテムがあり、すべて選択されている場合はヘッダーも選択
    if (items.length > 0 && selection[key].size === items.length) {
      headerSelection.add(key)
    } else if (items.length === 0) {
      // アイテムがない場合、ヘッダーのみ選択可能（デフォルトで選択）
      headerSelection.add(key)
    }
  }

  return headerSelection
}

/**
 * Selection と HeaderSelection を SoapContent の is_selected に変換
 */
const applySelectionToSoapContent = (
  originalSoapContent: SoapContent,
  selection: Selection
): SoapContent => {
  const newSoapContent: SoapContent = {
    subjectives: [],
    objectives: [],
    assessments: [],
    educational_plans: [],
    care_plans: [],
    observational_plans: [],
  }

  for (const key of SOAP_SECTION_KEYS) {
    const items = originalSoapContent[key] || []
    newSoapContent[key] = items.map((item: SoapContentItem, index: number) => ({
      ...item,
      is_selected: selection[key].has(index),
    }))
  }

  return newSoapContent
}

// ========================================
// useSelectionFromYakureki
// ========================================

interface UseSelectionFromYakurekiReturn {
  selection: Selection
  headerSelection: HeaderSelection
  isLoading: boolean
}

/**
 * yakurekiDetail から選択状態を計算するフック
 *
 * @description
 * TanStack Query の useQuery を使用してキャッシュを購読し、
 * soaps の is_selected フラグから Selection と HeaderSelection を計算。
 *
 * 重要: useQuery を使用することで、Optimistic Update でキャッシュが更新された際に
 * 自動的にコンポーネントが再レンダリングされる。
 *
 * 使用例:
 * ```tsx
 * const { selection, headerSelection, isLoading } = useSelectionFromYakureki(yakurekiId)
 * const isSelected = selection.subjectives.has(0) // index 0 が選択されているか
 * ```
 */
export const useSelectionFromYakureki = (
  yakurekiId: string | undefined
): UseSelectionFromYakurekiReturn => {
  // useQuery を使用してキャッシュを購読
  // 同じ queryKey を持つ usePatientDetailQuery とキャッシュを共有
  const { data: yakurekiDetail, isLoading } = useQuery({
    queryKey: queryKeys.yakureki.detail(yakurekiId ?? ''),
    queryFn: async () => {
      if (!yakurekiId) throw new Error('yakurekiId is required')
      const { data } = await getYakurekiDetail(yakurekiId)
      return data
    },
    enabled: !!yakurekiId,
    staleTime: 1000 * 60 * 5, // 5分キャッシュ（usePatientDetailQuery と同じ）
  })

  // soaps から id を除外して SoapContent を取得
  const soapContent = useMemo((): SoapContent | undefined => {
    const soaps = yakurekiDetail?.soaps
    if (!soaps) return undefined
    const { id: _id, ...rest } = soaps
    return rest as SoapContent
  }, [yakurekiDetail?.soaps])

  // Selection を計算
  const selection = useMemo(
    () => computeSelectionFromSoapContent(soapContent),
    [soapContent]
  )

  // HeaderSelection を計算
  const headerSelection = useMemo(
    () => computeHeaderSelectionFromSoapContent(soapContent, selection),
    [soapContent, selection]
  )

  return {
    selection,
    headerSelection,
    isLoading,
  }
}

// ========================================
// useToggleSelectionMutation
// ========================================

interface ToggleSelectionVariables {
  key: SoapSectionKeys
  index: number
}

/**
 * 単一アイテムの選択をトグルする Mutation
 *
 * @description
 * Optimistic Update パターンで即座に UI を更新し、
 * バックグラウンドでサーバーに同期。
 * エラー時は自動的にロールバック。
 *
 * 使用例:
 * ```tsx
 * const toggleSelection = useToggleSelectionMutation(yakurekiId)
 * toggleSelection.mutate({ key: 'subjectives', index: 0 })
 * ```
 */
export const useToggleSelectionMutation = (yakurekiId: string | undefined) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (_variables: ToggleSelectionVariables) => {
      if (!yakurekiId) throw new Error('yakurekiId is required')

      // onMutate で Optimistic Update 済みのキャッシュをそのまま送信
      // （トグルは onMutate で完了しているため、ここでは再トグルしない）
      const yakurekiDetail = queryClient.getQueryData<YakurekiDetail>(
        queryKeys.yakureki.detail(yakurekiId)
      )
      if (!yakurekiDetail?.soaps) throw new Error('soaps not found')

      const { id: soapId, ...soapContent } = yakurekiDetail.soaps

      await updateSOAP(yakurekiId, soapContent as SoapContent)

      return { soapId, updatedSoapContent: soapContent }
    },

    onMutate: async ({ key, index }) => {
      if (!yakurekiId) return

      // 進行中のクエリをキャンセル
      await queryClient.cancelQueries({
        queryKey: queryKeys.yakureki.detail(yakurekiId),
      })

      // スナップショット保存
      const previousYakurekiDetail = queryClient.getQueryData<YakurekiDetail>(
        queryKeys.yakureki.detail(yakurekiId)
      )

      // Optimistic Update
      queryClient.setQueryData<YakurekiDetail>(
        queryKeys.yakureki.detail(yakurekiId),
        (old) => {
          if (!old?.soaps) return old

          const { id: _soapId, ...soapContent } = old.soaps
          const items = soapContent[key] || []
          const updatedItems = items.map(
            (item: SoapContentItem, i: number) => ({
              ...item,
              is_selected: i === index ? !item.is_selected : item.is_selected,
            })
          )

          return {
            ...old,
            soaps: {
              ...old.soaps,
              [key]: updatedItems,
            },
          }
        }
      )

      return { previousYakurekiDetail }
    },

    onError: (_, __, context) => {
      // エラー時はロールバック
      if (context?.previousYakurekiDetail && yakurekiId) {
        queryClient.setQueryData(
          queryKeys.yakureki.detail(yakurekiId),
          context.previousYakurekiDetail
        )
      }
    },
    // onSettled で invalidateQueries を呼ばない（Optimistic Update を維持）
  })
}

// ========================================
// useSelectAllMutation
// ========================================

/**
 * 全選択 Mutation
 *
 * @description
 * すべてのセクションのすべてのアイテムを選択状態にする。
 * Optimistic Update で即座に UI を更新。
 *
 * 使用例:
 * ```tsx
 * const selectAll = useSelectAllMutation(yakurekiId)
 * selectAll.mutate()
 * ```
 */
export const useSelectAllMutation = (yakurekiId: string | undefined) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      if (!yakurekiId) throw new Error('yakurekiId is required')

      const yakurekiDetail = queryClient.getQueryData<YakurekiDetail>(
        queryKeys.yakureki.detail(yakurekiId)
      )
      if (!yakurekiDetail?.soaps) throw new Error('soaps not found')

      const { id: soapId, ...soapContent } = yakurekiDetail.soaps

      // 全選択
      const selection = createEmptySelection()
      for (const key of SOAP_SECTION_KEYS) {
        const items = soapContent[key] || []
        items.forEach((_: SoapContentItem, index: number) => {
          selection[key].add(index)
        })
      }

      const updatedSoapContent = applySelectionToSoapContent(
        soapContent,
        selection
      )
      await updateSOAP(yakurekiId, updatedSoapContent)

      return { soapId, updatedSoapContent }
    },

    onMutate: async () => {
      if (!yakurekiId) return

      await queryClient.cancelQueries({
        queryKey: queryKeys.yakureki.detail(yakurekiId),
      })

      const previousYakurekiDetail = queryClient.getQueryData<YakurekiDetail>(
        queryKeys.yakureki.detail(yakurekiId)
      )

      // Optimistic Update: 全アイテムを選択
      queryClient.setQueryData<YakurekiDetail>(
        queryKeys.yakureki.detail(yakurekiId),
        (old) => {
          if (!old?.soaps) return old

          const newSoaps = { ...old.soaps }
          for (const key of SOAP_SECTION_KEYS) {
            const items = newSoaps[key] || []
            newSoaps[key] = items.map((item: SoapContentItem) => ({
              ...item,
              is_selected: true,
            }))
          }

          return {
            ...old,
            soaps: newSoaps,
          }
        }
      )

      return { previousYakurekiDetail }
    },

    onError: (_, __, context) => {
      if (context?.previousYakurekiDetail && yakurekiId) {
        queryClient.setQueryData(
          queryKeys.yakureki.detail(yakurekiId),
          context.previousYakurekiDetail
        )
      }
    },
    // onSettled で invalidateQueries を呼ばない（Optimistic Update を維持）
  })
}

// ========================================
// useDeselectAllMutation
// ========================================

/**
 * 全解除 Mutation
 *
 * @description
 * すべてのセクションのすべてのアイテムを非選択状態にする。
 * Optimistic Update で即座に UI を更新。
 *
 * 使用例:
 * ```tsx
 * const deselectAll = useDeselectAllMutation(yakurekiId)
 * deselectAll.mutate()
 * ```
 */
export const useDeselectAllMutation = (yakurekiId: string | undefined) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      if (!yakurekiId) throw new Error('yakurekiId is required')

      const yakurekiDetail = queryClient.getQueryData<YakurekiDetail>(
        queryKeys.yakureki.detail(yakurekiId)
      )
      if (!yakurekiDetail?.soaps) throw new Error('soaps not found')

      const { id: soapId, ...soapContent } = yakurekiDetail.soaps

      // 全解除
      const selection = createEmptySelection()
      const updatedSoapContent = applySelectionToSoapContent(
        soapContent,
        selection
      )
      await updateSOAP(yakurekiId, updatedSoapContent)

      return { soapId, updatedSoapContent }
    },

    onMutate: async () => {
      if (!yakurekiId) return

      await queryClient.cancelQueries({
        queryKey: queryKeys.yakureki.detail(yakurekiId),
      })

      const previousYakurekiDetail = queryClient.getQueryData<YakurekiDetail>(
        queryKeys.yakureki.detail(yakurekiId)
      )

      // Optimistic Update: 全アイテムを非選択
      queryClient.setQueryData<YakurekiDetail>(
        queryKeys.yakureki.detail(yakurekiId),
        (old) => {
          if (!old?.soaps) return old

          const newSoaps = { ...old.soaps }
          for (const key of SOAP_SECTION_KEYS) {
            const items = newSoaps[key] || []
            newSoaps[key] = items.map((item: SoapContentItem) => ({
              ...item,
              is_selected: false,
            }))
          }

          return {
            ...old,
            soaps: newSoaps,
          }
        }
      )

      return { previousYakurekiDetail }
    },

    onError: (_, __, context) => {
      if (context?.previousYakurekiDetail && yakurekiId) {
        queryClient.setQueryData(
          queryKeys.yakureki.detail(yakurekiId),
          context.previousYakurekiDetail
        )
      }
    },
    // onSettled で invalidateQueries を呼ばない（Optimistic Update を維持）
  })
}

// ========================================
// useToggleHeaderMutation
// ========================================

interface ToggleHeaderVariables {
  key: SoapSectionKeys
}

/**
 * ヘッダー選択をトグルする Mutation
 *
 * @description
 * セクションヘッダーをクリックしたときに、
 * そのセクション内のすべてのアイテムを選択/解除する。
 *
 * - 一部選択中: 全選択にする
 * - 全選択中: 全解除にする
 * - 全解除中: 全選択にする
 *
 * 使用例:
 * ```tsx
 * const toggleHeader = useToggleHeaderMutation(yakurekiId)
 * toggleHeader.mutate({ key: 'subjectives' })
 * ```
 */
export const useToggleHeaderMutation = (yakurekiId: string | undefined) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (_variables: ToggleHeaderVariables) => {
      if (!yakurekiId) throw new Error('yakurekiId is required')

      // onMutate で Optimistic Update 済みのキャッシュをそのまま送信
      const yakurekiDetail = queryClient.getQueryData<YakurekiDetail>(
        queryKeys.yakureki.detail(yakurekiId)
      )
      if (!yakurekiDetail?.soaps) throw new Error('soaps not found')

      const { id: soapId, ...soapContent } = yakurekiDetail.soaps

      await updateSOAP(yakurekiId, soapContent as SoapContent)

      return { soapId, updatedSoapContent: soapContent }
    },

    onMutate: async ({ key }) => {
      if (!yakurekiId) return

      await queryClient.cancelQueries({
        queryKey: queryKeys.yakureki.detail(yakurekiId),
      })

      const previousYakurekiDetail = queryClient.getQueryData<YakurekiDetail>(
        queryKeys.yakureki.detail(yakurekiId)
      )

      // Optimistic Update
      queryClient.setQueryData<YakurekiDetail>(
        queryKeys.yakureki.detail(yakurekiId),
        (old) => {
          if (!old?.soaps) return old

          const items = old.soaps[key] || []
          const allSelected = items.every(
            (item: SoapContentItem) => item.is_selected
          )

          const updatedItems = items.map((item: SoapContentItem) => ({
            ...item,
            is_selected: !allSelected,
          }))

          return {
            ...old,
            soaps: {
              ...old.soaps,
              [key]: updatedItems,
            },
          }
        }
      )

      return { previousYakurekiDetail }
    },

    onError: (_, __, context) => {
      if (context?.previousYakurekiDetail && yakurekiId) {
        queryClient.setQueryData(
          queryKeys.yakureki.detail(yakurekiId),
          context.previousYakurekiDetail
        )
      }
    },
    // onSettled で invalidateQueries を呼ばない（Optimistic Update を維持）
  })
}

// ========================================
// useSetSelectionMutation
// ========================================

interface SetSelectionVariables {
  key: SoapSectionKeys
  indices: Set<number>
}

/**
 * セクションの選択状態を直接設定する Mutation
 *
 * @description
 * 薬品置換時など、複数アイテムの選択状態を一度に設定する必要がある場合に使用。
 *
 * 使用例:
 * ```tsx
 * const setSelection = useSetSelectionMutation(yakurekiId)
 * setSelection.mutate({ key: 'subjectives', indices: new Set([0, 2, 3]) })
 * ```
 */
export const useSetSelectionMutation = (yakurekiId: string | undefined) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ key, indices }: SetSelectionVariables) => {
      if (!yakurekiId) throw new Error('yakurekiId is required')

      const yakurekiDetail = queryClient.getQueryData<YakurekiDetail>(
        queryKeys.yakureki.detail(yakurekiId)
      )
      if (!yakurekiDetail?.soaps) throw new Error('soaps not found')

      const { id: soapId, ...soapContent } = yakurekiDetail.soaps

      // 指定されたセクションの選択状態を更新
      const selection = computeSelectionFromSoapContent(soapContent)
      selection[key] = new Set(indices)

      const updatedSoapContent = applySelectionToSoapContent(
        soapContent,
        selection
      )
      await updateSOAP(yakurekiId, updatedSoapContent)

      return { soapId, updatedSoapContent }
    },

    onMutate: async ({ key, indices }) => {
      if (!yakurekiId) return

      await queryClient.cancelQueries({
        queryKey: queryKeys.yakureki.detail(yakurekiId),
      })

      const previousYakurekiDetail = queryClient.getQueryData<YakurekiDetail>(
        queryKeys.yakureki.detail(yakurekiId)
      )

      // Optimistic Update
      queryClient.setQueryData<YakurekiDetail>(
        queryKeys.yakureki.detail(yakurekiId),
        (old) => {
          if (!old?.soaps) return old

          const items = old.soaps[key] || []
          const updatedItems = items.map(
            (item: SoapContentItem, index: number) => ({
              ...item,
              is_selected: indices.has(index),
            })
          )

          return {
            ...old,
            soaps: {
              ...old.soaps,
              [key]: updatedItems,
            },
          }
        }
      )

      return { previousYakurekiDetail }
    },

    onError: (_, __, context) => {
      if (context?.previousYakurekiDetail && yakurekiId) {
        queryClient.setQueryData(
          queryKeys.yakureki.detail(yakurekiId),
          context.previousYakurekiDetail
        )
      }
    },
    // onSettled で invalidateQueries を呼ばない（Optimistic Update を維持）
  })
}

// ========================================
// Utility Hooks
// ========================================

/**
 * 選択されたコンテンツがあるかチェックする
 */
export const useHasAnyContentSelected = (
  yakurekiId: string | undefined
): boolean => {
  const { selection } = useSelectionFromYakureki(yakurekiId)

  return useMemo(() => {
    for (const key of SOAP_SECTION_KEYS) {
      if (selection[key].size > 0) {
        return true
      }
    }
    return false
  }, [selection])
}

/**
 * 全選択状態かどうかチェックする
 */
export const useIsAllSelected = (yakurekiId: string | undefined): boolean => {
  const queryClient = useQueryClient()

  const yakurekiDetail = queryClient.getQueryData<YakurekiDetail>(
    queryKeys.yakureki.detail(yakurekiId ?? '')
  )

  return useMemo(() => {
    const soaps = yakurekiDetail?.soaps
    if (!soaps) return false

    for (const key of SOAP_SECTION_KEYS) {
      const items = soaps[key] || []
      const allSelected = items.every(
        (item: SoapContentItem) => item.is_selected
      )
      if (!allSelected) return false
    }
    return true
  }, [yakurekiDetail?.soaps])
}

/**
 * 特定アイテムが選択されているかチェックする
 */
export const useIsItemSelected = (
  yakurekiId: string | undefined,
  key: SoapSectionKeys,
  index: number
): boolean => {
  const { selection } = useSelectionFromYakureki(yakurekiId)
  return selection[key].has(index)
}

// ========================================
// Imperative API (for non-hook contexts)
// ========================================

/**
 * キャッシュから直接選択状態を取得する関数
 *
 * @description
 * フック外（コールバック関数内など）で選択状態を取得する必要がある場合に使用。
 * useSelectionFromYakureki のフック版を使用できない場面で使う。
 */
export const getSelectionFromCache = (
  queryClient: ReturnType<typeof useQueryClient>,
  yakurekiId: string
): { selection: Selection; headerSelection: HeaderSelection } => {
  const yakurekiDetail = queryClient.getQueryData<YakurekiDetail>(
    queryKeys.yakureki.detail(yakurekiId)
  )

  const soaps = yakurekiDetail?.soaps
  const soapContent = soaps
    ? (() => {
        const { id: _id, ...rest } = soaps
        return rest as SoapContent
      })()
    : undefined

  const selection = computeSelectionFromSoapContent(soapContent)
  const headerSelection = computeHeaderSelectionFromSoapContent(
    soapContent,
    selection
  )

  return { selection, headerSelection }
}
